import { useEffect, useState } from 'react';

const CACHE_KEY = 'adeeb-dominant-colors';
const memoryCache = new Map();

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    for (const [k, v] of Object.entries(obj)) memoryCache.set(k, v);
  } catch { /* ignore */ }
}
loadCache();

function persistCache() {
  try {
    const obj = Object.fromEntries(memoryCache);
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch { /* quota / disabled */ }
}

export function useDominantColor(src) {
  const [rgb, setRgb] = useState(() => (src && memoryCache.get(src)) || null);

  useEffect(() => {
    if (!src) return;
    const cached = memoryCache.get(src);
    if (cached) { setRgb(cached); return; }
    let alive = true;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = 24, h = 24;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < data.length; i += 4) {
          const R = data[i], G = data[i + 1], B = data[i + 2], A = data[i + 3];
          if (A < 128) continue;
          const max = Math.max(R, G, B), min = Math.min(R, G, B);
          if (max - min < 15) continue;
          if (max < 40 || min > 230) continue;
          r += R; g += G; b += B; n++;
        }
        if (!alive) return;
        if (n > 0) {
          const value = `${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)}`;
          memoryCache.set(src, value);
          persistCache();
          setRgb(value);
        }
      } catch { /* CORS */ }
    };
    img.src = src;
    return () => { alive = false; };
  }, [src]);

  return rgb;
}
